/**
 * Payments settings container (DNKADM-0110) — the tabbed Owner/Admin
 * Configuration destination at `/settings/payments`, `RequireAdmin`-gated by the route.
 *
 * Hosts two MUI sub-tabs, routed at
 * `settings/payments/{overview|payment-methods}` (the preserved `connection` route
 * forwards OAuth returns into Overview's connection drawer — see `router.tsx`):
 *  - **Overview** — the calm status dashboard and connection-editor drawer.
 *  - **Payment methods** — the §E payment-methods CRUD (DNKADM-0091). Co-located
 *    here because `card` methods derive from the connections configured on the
 *    Overview drawer.
 *
 * The active tab is derived from the URL and each tab navigates to its sub-path, so
 * the tabs are deep-linkable and back/forward works; the tab bodies render through
 * an `<Outlet />`. Mirrors the membership-credits sub-tab container pattern.
 */
import { Box, Tab, Tabs } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { COURT_GREEN } from '@/theme';

/** The two human-facing sub-tabs. The OAuth-only `connection` route is intentionally absent. */
const SUB_TABS = [
  { path: 'overview', label: 'Overview' },
  { path: 'payment-methods', label: 'Manual Payment methods' },
] as const;

type SubTabPath = (typeof SUB_TABS)[number]['path'];

/**
 * Resolve the active sub-tab from the current URL. The container only renders under
 * `/settings/payments/<path>`; an unknown/trailing segment falls back to `overview`
 * (the index landing tab), so a tab is always selected.
 */
function activeTabFrom(pathname: string): SubTabPath {
  const match = SUB_TABS.find(
    (t) =>
      pathname === `/settings/payments/${t.path}` ||
      pathname.startsWith(`/settings/payments/${t.path}/`),
  );
  return match?.path ?? 'overview';
}

export function CreditCardSettingsScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = activeTabFrom(location.pathname);

  return (
    <Box>
      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_e, value: SubTabPath) => navigate(value)}
        aria-label="Payments sections"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 2.5,
          '& .MuiTabs-indicator': { bgcolor: COURT_GREEN },
          '& .Mui-selected': { color: `${COURT_GREEN} !important` },
        }}
      >
        {SUB_TABS.map((t) => (
          <Tab key={t.path} value={t.path} label={t.label} />
        ))}
      </Tabs>

      <Outlet />
    </Box>
  );
}
